import React, { useState } from "react";
import { X, Trash2, Minus, Plus } from "lucide-react";
import { useBasket } from "../contexts/BasketContext";
import { submitEnquiry, readUtmParams } from "../lib/api";

export default function WineBasket() {
  const { open, setOpen, items, totalBottles, totalPrice, setQty, remove, clear } = useBasket();
  const [step, setStep] = useState("basket");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", pickup: "Osobní odběr", consent: false, adult: false, website: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.adult) {
      setStatus({ loading: false, ok: false, error: "Potvrďte prosím, že je vám alespoň 18 let." });
      return;
    }
    if (items.length === 0) {
      setStatus({ loading: false, ok: false, error: "Přidejte prosím alespoň jednu láhev." });
      return;
    }
    setStatus({ loading: true, ok: false, error: "" });
    const res = await submitEnquiry({
      type: "objednavka_vina",
      name: form.name,
      email: form.email,
      phone: form.phone,
      message: form.message,
      wine_pickup: form.pickup,
      wines: items.map((i) => ({ slug: i.slug, name: i.name, quantity: i.quantity })),
      consent_privacy: true,
      consent_adult: form.adult,
      website: form.website,
      ...readUtmParams(),
    });
    if (res.ok) {
      setStatus({ loading: false, ok: true, error: "" });
      clear();
    } else {
      setStatus({ loading: false, ok: false, error: res.error });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-label="Poptávka vína" data-testid="wine-basket-drawer">
      <div className="absolute inset-0 bg-charcoal/50" onClick={() => setOpen(false)} />
      <aside className="relative w-full sm:w-[440px] h-full bg-ivory shadow-2xl flex flex-col">
        <header className="flex items-center justify-between p-5 border-b border-charcoal/10">
          <div>
            <div className="text-[10px] tracking-[.25em] uppercase text-burgundy">Poptávka vína</div>
            <h2 className="font-serif text-xl">{totalBottles} {totalBottles === 1 ? "láhev" : totalBottles < 5 ? "láhve" : "lahví"}</h2>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Zavřít" data-testid="basket-close-btn" className="p-2 hover:bg-cream"><X size={20} /></button>
        </header>

        {status.ok ? (
          <div className="p-6 flex-1 overflow-y-auto">
            <div className="bg-cream border border-burgundy/20 p-5">
              <div className="font-serif text-xl text-burgundy">Děkujeme, poptávku máme.</div>
              <p className="mt-2 text-sm text-charcoal/80">Ozveme se vám s potvrzením dostupnosti, způsobu převzetí a konečné ceny.</p>
              <button onClick={() => { setStep("basket"); setStatus({ loading: false, ok: false, error: "" }); setOpen(false); }} className="mt-4 text-sm underline">Zavřít</button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <p className="text-sm text-charcoal/70">Poptávka je zatím prázdná. Přidejte lahve z <a href="/vina" className="underline text-burgundy">nabídky vín</a>.</p>
              ) : (
                <ul className="divide-y divide-charcoal/10">
                  {items.map((i) => (
                    <li key={i.slug} className="py-4 flex items-start gap-3" data-testid={`basket-item-${i.slug}`}>
                      <div className="flex-1">
                        <div className="font-serif text-lg">{i.name}</div>
                        <div className="text-xs text-charcoal/60">{i.price} Kč / láhev</div>
                        <div className="mt-2 inline-flex items-center border border-charcoal/20">
                          <button onClick={() => setQty(i.slug, i.quantity - 1)} className="p-1.5 hover:bg-cream" aria-label="Snížit"><Minus size={12} /></button>
                          <span className="w-8 text-center text-sm">{i.quantity}</span>
                          <button onClick={() => setQty(i.slug, i.quantity + 1)} className="p-1.5 hover:bg-cream" aria-label="Zvýšit"><Plus size={12} /></button>
                        </div>
                      </div>
                      <button onClick={() => remove(i.slug)} aria-label="Odebrat" className="text-charcoal/50 hover:text-burgundy p-1"><Trash2 size={16} /></button>
                    </li>
                  ))}
                </ul>
              )}

              {items.length > 0 && (
                <>
                  <div className="mt-4 flex justify-between text-sm border-t border-charcoal/10 pt-4">
                    <span className="text-charcoal/70">Orientační cena</span>
                    <span className="font-serif text-lg text-burgundy">{totalPrice.toLocaleString("cs-CZ")} Kč</span>
                  </div>
                  <p className="mt-3 text-xs text-charcoal/60 leading-relaxed">
                    Odesláním formuláře nevzniká automaticky potvrzená objednávka. Dostupnost,
                    způsob převzetí a konečnou cenu vám potvrdíme.
                  </p>

                  <form onSubmit={submit} className="mt-5 space-y-3" data-testid="wine-enquiry-form">
                    <input type="text" name="website" value={form.website} onChange={(e) => update("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                    <input required placeholder="Jméno a příjmení" value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full border border-charcoal/20 bg-ivory px-3 py-2.5 text-sm" data-testid="wine-form-name" />
                    <input required type="email" placeholder="E-mail" value={form.email} onChange={(e) => update("email", e.target.value)} className="w-full border border-charcoal/20 bg-ivory px-3 py-2.5 text-sm" data-testid="wine-form-email" />
                    <input required placeholder="Telefon" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="w-full border border-charcoal/20 bg-ivory px-3 py-2.5 text-sm" data-testid="wine-form-phone" />
                    <select value={form.pickup} onChange={(e) => update("pickup", e.target.value)} className="w-full border border-charcoal/20 bg-ivory px-3 py-2.5 text-sm">
                      <option>Osobní odběr</option>
                      <option>Domluvit způsob předání</option>
                    </select>
                    <textarea placeholder="Zpráva (nepovinné)" rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} className="w-full border border-charcoal/20 bg-ivory px-3 py-2.5 text-sm" />
                    <label className="flex gap-2 text-xs text-charcoal/70" data-testid="wine-form-privacy-notice">
                      <span>
                        Odesláním formuláře berete na vědomí zpracování uvedených údajů za účelem
                        vyřízení této poptávky. Podrobnosti najdete v&nbsp;
                        <a href="/ochrana-osobnich-udaju" className="underline hover:text-burgundy">ochraně osobních údajů</a>.
                      </span>
                    </label>
                    <label className="flex gap-2 text-xs text-charcoal/75">
                      <input type="checkbox" checked={form.adult} onChange={(e) => update("adult", e.target.checked)} data-testid="wine-form-adult" />
                      <span>Potvrzuji, že jsem starší 18 let.</span>
                    </label>
                    {status.error && <div className="text-sm text-red-700" role="alert">{status.error}</div>}
                    <button disabled={status.loading} type="submit" data-testid="wine-form-submit" className="w-full bg-burgundy hover:bg-burgundy-hover text-ivory text-sm py-3 tracking-wide disabled:opacity-60">
                      {status.loading ? "Odesílám…" : "Odeslat poptávku"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
